/**
 * 待办红点计数 Provider（S2-2）
 *
 * 持有待办中心 TO_DO 计数并下发；轮询策略：
 *  - 挂载即拉取一次；
 *  - 60s 定时轮询；
 *  - window focus 时刷新（切回标签页立即更新）；
 *  - 暴露 refresh() 供「进入待办中心 / 推进回退成功后」手动刷新。
 *
 * 权限门：仅当 permissions 含 trade:task:list 或 *:* 才调 getTodoCount，
 * 避免无权限角色产生 403 噪音（AppContext 暴露 permissions）。
 */

import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { fulfillmentApi } from '@/api/fulfillment';
import { useAppContext } from '@/contexts/AppContext';

const REFRESH_INTERVAL_MS = 60_000;

export interface TodoCountContextValue {
  count: number;
  refresh: () => Promise<void>;
}

const TodoCountContext = createContext<TodoCountContextValue>({
  count: 0,
  refresh: async () => {},
});

/** 权限门：有 trade:task:list 或 *:* 才允许拉取计数 */
function canFetchCount(permissions: string[]): boolean {
  return permissions.includes('trade:task:list') || permissions.includes('*:*');
}

export function TodoCountProvider({ children }: { children: React.ReactNode }) {
  const { permissions } = useAppContext();
  const [count, setCount] = useState<number>(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refresh = useCallback(async () => {
    if (!canFetchCount(permissions)) {
      setCount(0);
      return;
    }
    try {
      const value = await fulfillmentApi.getTodoCount();
      setCount(typeof value === 'number' ? value : 0);
    } catch {
      // 静默失败
    }
  }, [permissions]);

  // 挂载时拉取一次 + 60s 定时轮询
  useEffect(() => {
    refresh();
    timerRef.current = setInterval(refresh, REFRESH_INTERVAL_MS);
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [refresh]);

  // window focus 刷新（切回标签页立即更新）
  useEffect(() => {
    const onFocus = () => {
      refresh();
    };
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
    };
  }, [refresh]);

  return (
    <TodoCountContext.Provider value={{ count, refresh }}>
      {children}
    </TodoCountContext.Provider>
  );
}

export { TodoCountContext };
export default TodoCountProvider;
